import { useState, useEffect, useCallback } from 'react';
import { CheckBox, Popup, Tooltip } from 'devextreme-react';
import { Button, TextBox } from 'devextreme-react';
import { useScreenSize } from '../../../../../utils/media-query';
import { useAppSelector } from '../../../../../contexts/hooks';
import { newToast } from '../../../..';
import { ToolbarItem } from 'devextreme-react/popup';
import { SendResultByEmail } from '../../../../../types/types.orders';
import { UseApiResults } from '../../../../../api/api.results';
import { useApiFavorites } from '../../../../../api/api.Favorites';

interface FavoriteEmail {
  Cf_Id: number;
  Cf_Correo: string;
  Cf_Nombre: string;
}

interface SendMailResulsProps {
  visible: boolean;
  setVisible: ({ visible }) => void;
  lstStudies: number[] | undefined;
  patientData?: any;
  orderData?: any;
}

export const SendMailResuls = ({ visible, setVisible, lstStudies, patientData: propPatientData, orderData: propOrderData }: SendMailResulsProps) => {
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const EmailServiceFieldPatient = useAppSelector((x) => x.configuration.Data?.Config_General.find((cfg) => cfg.Cgn_Nombre === 'EmailServiceFieldPatient')?.Cgn_Resultado);
  const info = useAppSelector((x) => x.configuration.Data?.SystemFields);
  const globalOrderData = useAppSelector((x) => x.globalOrders.CurrentOrder.OrderData);
  const globalPatientData = useAppSelector((x) => x.globalOrders.CurrentOrder.PatientData);
  const attachments = useAppSelector((x) => x.globalOrders.CurrentOrder.Attachments);
  const [attachmentsStudies, setAttachmentsStudies] = useState(true);
  const [attachment, setAttachment] = useState(true);
  const [loading, setLoading] = useState(false);
  const [emailFields, setEmailFields] = useState<{ id: string; value: string }[]>([]);
  const [emailInput, setEmailInput] = useState<string>('');
  const { senResultByMail } = UseApiResults();
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  const { GetAllCorreosFavoritos, CreateCorreoFavorito, DeleteCorreoFavorito } = useApiFavorites();
  
  // Estado para favoritos
  const [favorites, setFavorites] = useState<FavoriteEmail[]>([]);
  const [showFavorites, setShowFavorites] = useState(false);
  const [showAddFavoritePrompt, setShowAddFavoritePrompt] = useState(false);
  const [pendingEmailToFavorite, setPendingEmailToFavorite] = useState<string>('');
  const [favoriteNameInput, setFavoriteNameInput] = useState<string>('');

  // Cargar favoritos
  const loadFavorites = useCallback(async () => {
    const data = await GetAllCorreosFavoritos();
    setFavorites(data || []);
  }, []);

  useEffect(() => {
    if (visible) {
      loadFavorites();
    }
  }, [visible, loadFavorites]);

  useEffect(() => {
    if (!visible) return;

    // Usar los datos proporcionados por props o caer en los datos globales
    const effectiveOrderData = propOrderData || globalOrderData;
    const effectivePatientData = propPatientData || globalPatientData;

    const matchedField = info?.find((field) => field.Dfc_Equivalencia === EmailServiceFieldPatient);

    if (matchedField) {
      if (matchedField.Dfc_Tabla === 'Ordenes' && effectiveOrderData?.AditionalFields) {
        const emailList = effectiveOrderData.AditionalFields[matchedField.Dfc_Equivalencia] || [];
        const formattedEmails = Array.isArray(emailList) ? emailList : emailList ? [emailList] : [];
        const emailFieldsData = formattedEmails.map((email, index) => ({
          id: `email_${index}_${Date.now()}`,
          value: email
        }));
        setEmailFields(emailFieldsData);
      } else if (matchedField.Dfc_Tabla === 'Pacientes' && effectivePatientData?.AditionalFields) {
        const emailList = effectivePatientData.AditionalFields[matchedField.Dfc_Equivalencia] || [];
        const formattedEmails = Array.isArray(emailList) ? emailList : emailList ? [emailList] : [];
        const emailFieldsData = formattedEmails.map((email, index) => ({
          id: `email_${index}_${Date.now()}`,
          value: email
        }));
        setEmailFields(emailFieldsData);
      } else {
        setEmailFields([]);
      }
    } else {
      setEmailFields([]);
    }
  }, [visible, propPatientData, propOrderData]);

  const handleClose = () => {
    setVisible({ visible: false });
  };

  const handleAddEmail = () => {
    const trimmedEmail = emailInput.trim();
    if (trimmedEmail && validateEmail(trimmedEmail)) {
      const existingField = emailFields.find(field => field.value === trimmedEmail);
      if (!existingField) {
        const newEmailField = {
          id: `email_${emailFields.length}_${Date.now()}`,
          value: trimmedEmail
        };
        setEmailFields([...emailFields, newEmailField]);
        setEmailInput('');
        
        // Verificar si el correo ya está en favoritos
        const isInFavorites = favorites.some(fav => fav.Cf_Correo.toLowerCase() === trimmedEmail.toLowerCase());
        if (!isInFavorites) {
          setPendingEmailToFavorite(trimmedEmail);
          setShowAddFavoritePrompt(true);
        }
      } else {
        newToast('WARNING', 'El correo electrónico ya está en la lista.', 5);
      }
    } else {
      newToast('WARNING', 'El correo electrónico no es válido.', 5);
    }
  };

  // Agregar correo desde favoritos a la lista
  const handleAddFromFavorite = (favorite: FavoriteEmail) => {
    const existingField = emailFields.find(field => field.value === favorite.Cf_Correo);
    if (!existingField) {
      const newEmailField = {
        id: `email_${emailFields.length}_${Date.now()}`,
        value: favorite.Cf_Correo
      };
      setEmailFields([...emailFields, newEmailField]);
    } else {
      newToast('INFO', 'El correo ya está en la lista de destinatarios.', 3);
    }
  };

  // Eliminar favorito
  const handleDeleteFavorite = async (e: React.MouseEvent, favorite: FavoriteEmail) => {
    e.stopPropagation();
    const success = await DeleteCorreoFavorito(favorite.Cf_Id);
    if (success) {
      newToast('SUCCESS', 'Favorito eliminado correctamente.', 3);
      loadFavorites();
    }
  };

  // Confirmar agregar a favoritos
  const handleConfirmAddToFavorites = async () => {
    if (!favoriteNameInput.trim()) {
      newToast('WARNING', 'Debe ingresar un nombre para el favorito.', 3);
      return;
    }
    if (pendingEmailToFavorite) {
      const name = favoriteNameInput.trim();
      const success = await CreateCorreoFavorito(pendingEmailToFavorite, name);
      if (success) {
        newToast('SUCCESS', 'Correo agregado a favoritos.', 3);
        loadFavorites();
      }
    }
    setShowAddFavoritePrompt(false);
    setPendingEmailToFavorite('');
    setFavoriteNameInput('');
  };

  // Cancelar agregar a favoritos
  const handleCancelAddToFavorites = () => {
    setShowAddFavoritePrompt(false);
    setPendingEmailToFavorite('');
    setFavoriteNameInput('');
  };

  // Función para eliminar un campo de email
  const handleRemoveEmailField = (id: string) => {
    setEmailFields(emailFields.filter(field => field.id !== id));
  };

  const handleChangeAttachmentStudies = (e: any) => {
    setAttachmentsStudies(e);
  };

  const handleChangeAttachment = (e: any) => {        
    setAttachment(e);
  };

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const onSaveClick = () => {
    if (!idOrd || idOrd === -1) {
      return;
    }
    if (emailFields.length === 0) {
      newToast('WARNING', 'Debe ingresar al menos un correo de destino', 5);
      return;
    }

    const emailAddresses = emailFields.map(field => field.value);

    const dataToSend: SendResultByEmail = {
      IdOrder: idOrd,
      Destinators: emailAddresses,
      Studies: lstStudies || [],
      IncludeAttachments: attachment,
      IncludeAttachmentsStudies: attachmentsStudies,
    };
    newToast('INFO', 'Se está enviando el correo electrónico, esto puede tardar algunos minutos.', 5);
    senResultByMail(dataToSend);

    setVisible({ visible: false });
  };

  return (
    <Popup visible={visible} fullScreen={isMedium || isSmall || isXSmall} width={600} maxHeight={700} title='Enviar por correo electrónico' onHiding={handleClose} showCloseButton={true}>
      <div>
        {/* Sección de Favoritos - Colapsable */}
        {favorites.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div 
              onClick={() => setShowFavorites(!showFavorites)}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                padding: '8px 0',
                color: '#666',
                fontSize: '13px'
              }}
            >
              <i className={`dx-icon-${showFavorites ? 'chevrondown' : 'chevronright'}`} style={{ marginRight: '6px' }} />
              <i className='dx-icon-favorites' style={{ marginRight: '6px', color: '#ffc107' }} />
              <span>Favoritos ({favorites.length})</span>
            </div>
            
            {showFavorites && (
              <div style={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: '6px', 
                padding: '6px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                marginTop: '4px'
              }}>
                {favorites.map((fav) => {
                  const isAdded = emailFields.some(field => field.value === fav.Cf_Correo);
                  return (
                    <div
                      key={fav.Cf_Id}
                      onClick={() => !isAdded && handleAddFromFavorite(fav)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '3px 8px',
                        backgroundColor: isAdded ? '#e8f5e9' : '#fff',
                        border: `1px solid ${isAdded ? '#4caf50' : '#ddd'}`,
                        borderRadius: '12px',
                        fontSize: '11px',
                        cursor: isAdded ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: isAdded ? 0.7 : 1,
                        lineHeight: '1.2'
                      }}
                      title={isAdded ? 'Ya agregado' : `Clic para agregar: ${fav.Cf_Correo}`}
                    >
                      {isAdded && <i className='dx-icon-check' style={{ marginRight: '3px', color: '#4caf50', fontSize: '10px' }} />}
                      <span style={{ marginRight: '4px' }}>{fav.Cf_Nombre || fav.Cf_Correo}</span>
                      <i 
                        className='dx-icon-trash'
                        onClick={(e) => handleDeleteFavorite(e, fav)}
                        style={{ 
                          cursor: 'pointer', 
                          color: '#999',
                          fontSize: '10px'
                        }}
                        title='Eliminar de favoritos'
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Input para agregar correo */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <TextBox id='emailInput' value={emailInput} onValueChanged={(e) => setEmailInput(e.value)} label='Agregar correo' labelMode='static' placeholder='Escribe un correo' onEnterKey={handleAddEmail} style={{ flex: 1, marginRight: '8px' }} />
          <Button stylingMode='text' icon='add' onClick={handleAddEmail} type='default' />
        </div>

        {/* Prompt para agregar a favoritos - Inline sutil */}
        {showAddFavoritePrompt && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '6px 10px',
            backgroundColor: '#fffde7',
            borderRadius: '4px',
            marginBottom: '12px',
            border: '1px solid #fff59d',
            fontSize: '12px'
          }}>
            <i className='dx-icon-favorites' style={{ color: '#ffc107', fontSize: '14px' }} />
            <span style={{ color: '#666', whiteSpace: 'nowrap' }}>¿Guardar <strong>{pendingEmailToFavorite}</strong>?</span>
            <TextBox 
              value={favoriteNameInput}
              onValueChanged={(e) => setFavoriteNameInput(e.value)}
              label='Nombre'
              labelMode='static'
              placeholder='Escribe un nombre'
              style={{ flex: 1 }}
            />
            <Button icon='check' stylingMode='text' type='success' onClick={handleConfirmAddToFavorites} hint='Agregar a favoritos' />
            <Button icon='close' stylingMode='text' onClick={handleCancelAddToFavorites} hint='Cancelar' />
          </div>
        )}

        {/* Lista de correos agregados */}
        <div className='widget-container'>
          {emailFields.map((field) => (
            <div key={field.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
              <TextBox
                value={field.value}
                readOnly={true}
                placeholder='Correo electrónico'
                style={{ flex: 1, marginRight: '8px' }}
              />
              <Button 
                stylingMode='text' 
                icon='remove' 
                onClick={() => handleRemoveEmailField(field.id)} 
                type='danger' 
              />
            </div>
          ))}
          {emailFields.length > 0 && (
            <div style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
              Los correos electrónicos se enviarán a estas direcciones.
            </div>
          )}
        </div>
      </div>
      {attachments && attachments.length > 0 && (
        <>
          <hr />
          <h5>Incluir archivos adjuntos?</h5>
          <p style={{ marginTop: -20 }}>Esta orden contiene archivos adjuntos que pueden ser enviados por correo electrónico.</p>
        </>
      )}
      {attachments && attachments.filter((x) => x.Oad_Es_Id).length > 0 && (
        <div className='widget-container'>
          <div className='dx-field' id='attachmentsest'>
            <div className='dx-field-label'>Incluir adjuntos de estudios?</div>
            <div className='dx-field-value'>
              <CheckBox defaultValue={true} elementAttr={{ 'aria-label': 'Checked' }} onValueChange={handleChangeAttachmentStudies} />
            </div>
          </div>
          <Tooltip target={'#attachmentsest'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
            Adjuntos que están asociados a un estudio.
          </Tooltip>
        </div>
      )}

      {attachments && attachments.filter((x) => !x.Oad_Es_Id).length > 0 && (
        <div className='widget-container' style={{ marginTop: -15 }}>
          <div className='dx-field' id='attachments'>
            <div className='dx-field-label'>Incluir archivos adjuntos no asociados a estudios?</div>
            <div className='dx-field-value'>
              <CheckBox defaultValue={true} elementAttr={{ 'aria-label': 'Checked' }} onValueChange={handleChangeAttachment} />
            </div>
          </div>
          <Tooltip target={'#attachments'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
            Adjuntos que no están asociados a un estudio.
          </Tooltip>
        </div>
      )}

      <ToolbarItem toolbar='bottom' location='after'>
        <div className={`form-popup-buttons-container `}>
          <Button stylingMode='contained' text='Cancelar' type={'normal'} onClick={handleClose} />
          <Button stylingMode='contained' text='Enviar' type={loading ? 'normal' : 'default'} disabled={loading} onClick={onSaveClick} />
        </div>
      </ToolbarItem>
    </Popup>
  );
};
